import { useState, useEffect, useCallback } from "react";
import { api } from "../App";
import { getApiPrefix } from "../utils/apiHelper";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SearchableSelect } from "../components/ui/searchable-select";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import DataTable from "../components/ui/data-table";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { PageLoader } from "../components/ui/page-loader";
import { Loader2, Plus, Pencil, Trash2, Percent, Building, Wallet, Clock } from "lucide-react";
import { useTranslation } from "../contexts/I18nContext";

export default function CategorieImmobilizzazioniPage() {
  const { t } = useTranslation();
  const [categorie, setCategorie] = useState([]);
  const [conti, setConti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });
  const [metodoAmm, setMetodoAmm] = useState("percentuale");
  
  const [form, setForm] = useState({
    nome: "",
    quota_annuale_percentuale: "",
    descrizione: "",
    conto_id: "",
    durata_mesi: ""
  });

  const fetchData = useCallback(async () => {
    try {
      const prefix = getApiPrefix();
      const [catRes, contiRes, metodoRes] = await Promise.all([
        api.get(`${prefix}/categorie-immobilizzazioni`),
        api.get(`${prefix}/accounts`),
        api.get(`${prefix}/settings/metodo-ammortamento`)
      ]);
      setCategorie(catRes.data);
      setConti(contiRes.data);
      setMetodoAmm(metodoRes.data?.metodo || "percentuale");
    } catch (err) {
      handleApiError(err, t("errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openAddDialog = () => {
    setEditingId(null);
    setForm({ nome: "", quota_annuale_percentuale: "", descrizione: "", conto_id: "", durata_mesi: "" });
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingId(item.id);
    setForm({
      nome: item.nome || "",
      quota_annuale_percentuale: String(item.quota_annuale_percentuale || ""),
      descrizione: item.descrizione || "",
      conto_id: item.conto_id || "",
      durata_mesi: item.durata_mesi ? String(item.durata_mesi) : ""
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.nome || !form.quota_annuale_percentuale) {
      handleApiError({ message: t("errors.requiredNameRate") });
      return;
    }
    
    const quota = parseFloat(form.quota_annuale_percentuale);
    if (isNaN(quota) || quota <= 0 || quota > 100) {
      handleApiError({ message: t("errors.requiredAnnualRate") });
      return;
    }
    
    try {
      const params = new URLSearchParams({
        nome: form.nome,
        quota_annuale_percentuale: String(quota)
      });
      if (form.descrizione) params.append("descrizione", form.descrizione);
      if (form.conto_id && form.conto_id !== "__none__") {
        params.append("conto_id", form.conto_id);
      }
      if (form.durata_mesi) params.append("durata_mesi", form.durata_mesi);
      
      const prefix = getApiPrefix();
      if (editingId) {
        await api.put(`${prefix}/categorie-immobilizzazioni/${editingId}?${params.toString()}`);
        showSuccess(t("success.categoryUpdated"));
      } else {
        await api.post(`${prefix}/categorie-immobilizzazioni?${params.toString()}`);
        showSuccess(t("success.categoryCreated"));
      }
      
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.saveFailed"));
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`${getApiPrefix()}/categorie-immobilizzazioni/${deleteDialog.item.id}`);
      showSuccess(t("success.categoryDeleted"));
      setDeleteDialog({ open: false, item: null });
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.deleteFailed"));
    }
  };

  const handleBulkDelete = async (ids) => {
    let deleted = 0;
    const prefix = getApiPrefix();
    for (const id of ids) {
      try {
        await api.delete(`${prefix}/categorie-immobilizzazioni/${id}`);
        deleted++;
      } catch (err) {
        
      }
    }
    showSuccess(`${deleted} categorie eliminate`);
    fetchData();
  };

  const handleChangeMetodo = async (newMetodo) => {
    try {
      await api.put(`${getApiPrefix()}/settings/metodo-ammortamento?metodo=${newMetodo}`);
      setMetodoAmm(newMetodo);
      showSuccess(`Metodo ammortamento: ${newMetodo === 'percentuale' ? 'Percentuale annuale' : 'Lineare per mesi'}`);
    } catch (err) { handleApiError(err); }
  };

  const columns = [
    {
      key: 'nome',
      label: 'Nome Categoria',
      render: (value) => <span className="font-medium">{value}</span>
    },
    {
      key: 'quota_annuale_percentuale',
      label: 'Quota Annuale',
      render: (value) => (
        <Badge variant="outline" className="font-mono">
          <Percent className="h-3 w-3 mr-1" />
          {value}%
        </Badge>
      )
    },
    ...(metodoAmm === 'lineare_mesi' ? [{
      key: 'durata_mesi',
      label: 'Durata (mesi)',
      render: (value) => value ? (
        <Badge variant="secondary" className="font-mono">
          <Clock className="h-3 w-3 mr-1" />
          {value} mesi
        </Badge>
      ) : <span className="text-muted-foreground text-xs">Non impostata</span>
    }] : [{
      key: 'anni_ammortamento',
      label: 'Anni Ammortamento',
      getValue: (row) => row.quota_annuale_percentuale > 0 ? Math.ceil(100 / row.quota_annuale_percentuale) : "-",
      render: (value) => <span className="text-muted-foreground">{value} anni</span>
    }]),
    {
      key: 'conto_nome',
      label: 'Conto Ammortamento',
      render: (value, row) => value ? (
        <div className="flex items-center gap-1">
          <Wallet className="h-3 w-3 text-muted-foreground" />
          <span>{row.conto_codice && `[${row.conto_codice}] `}{value}</span>
        </div>
      ) : <span className="text-muted-foreground">-</span>
    },
    {
      key: 'descrizione',
      label: t('common.description'),
      render: (value) => (
        <span className="truncate max-w-[200px] block text-muted-foreground">
          {value || "-"}
        </span>
      )
    },
    {
      key: 'actions',
      label: t('common.actions'),
      align: 'right',
      sortable: false,
      filterable: false,
      exportable: false,
      render: (_, row) => (
        <div className="flex justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openEditDialog(row)}
            data-testid={`edit-${row.id}`}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDeleteDialog({ open: true, item: row })}
            className="text-destructive"
            data-testid={`delete-${row.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )
    }
  ];

  if (loading) {
    return <PageLoader variant="section" text={t("common.loading")} />;
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="categorie-immobilizzazioni-page">
      <div>
        <h1 className="text-2xl font-bold">{t("nav.assetCategories")}</h1>
        <p className="text-muted-foreground">Gestisci le categorie di beni ammortizzabili con le relative quote annuali</p>
      </div>

      {/* Metodo Ammortamento */}
      <Card className="border-amber-200">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">Metodo di Calcolo Ammortamento</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {metodoAmm === 'percentuale' 
                  ? "Quota annuale percentuale sul valore del bene (primo anno al 50%)" 
                  : "Quota lineare: costo / durata mesi, dalla data fattura"}
              </p>
            </div>
            <div className="flex gap-1 p-1 bg-muted rounded-lg">
              <Button variant={metodoAmm === 'percentuale' ? 'default' : 'ghost'} size="sm" className="text-xs h-8" onClick={() => handleChangeMetodo('percentuale')}>
                <Percent className="h-3 w-3 mr-1" /> Percentuale
              </Button>
              <Button variant={metodoAmm === 'lineare_mesi' ? 'default' : 'ghost'} size="sm" className="text-xs h-8" onClick={() => handleChangeMetodo('lineare_mesi')}>
                <Clock className="h-3 w-3 mr-1" /> Lineare per Mesi
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Elenco Categorie</CardTitle>
          <CardDescription>Definisci le tipologie di beni e le relative quote di ammortamento</CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={categorie}
            onDelete={handleBulkDelete}
            idField="id"
            emptyMessage="Nessuna categoria definita"
            exportFileName="categorie_immobilizzazioni"
            toolbar={
              <Button onClick={openAddDialog} data-testid="add-categoria-btn">
                <Plus className="h-4 w-4 mr-2" />
                Nuova Categoria
              </Button>
            }
          />
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? t("categories.editCategory") : t("categories.newCategory")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome Categoria *</Label>
              <Input
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                placeholder="Es: Automezzi, Attrezzature, Mobili e arredi..."
                data-testid="input-nome"
              />
            </div>
            <div className="space-y-2">
              <Label>Quota Annuale % *</Label>
              <div className="relative">
                <Input
                  type="number"
                  step="0.5"
                  min="0.5"
                  max="100"
                  value={form.quota_annuale_percentuale}
                  onChange={(e) => setForm({ ...form, quota_annuale_percentuale: e.target.value })}
                  placeholder="Es: 25"
                  className="pr-8"
                  data-testid="input-quota"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
              </div>
              {form.quota_annuale_percentuale && parseFloat(form.quota_annuale_percentuale) > 0 && (
                <p className="text-xs text-muted-foreground">
                  Ammortamento completo in ~{Math.ceil(100 / parseFloat(form.quota_annuale_percentuale))} anni
                </p>
              )}
            </div>
            {metodoAmm === 'lineare_mesi' && (
              <div className="space-y-2">
                <Label>Durata Ammortamento (mesi) *</Label>
                <div className="relative">
                  <Input
                    type="number"
                    min="1"
                    max="600"
                    value={form.durata_mesi}
                    onChange={(e) => setForm({ ...form, durata_mesi: e.target.value })}
                    placeholder="Es: 60"
                    data-testid="input-durata-mesi"
                  />
                </div>
                {form.durata_mesi && parseInt(form.durata_mesi) > 0 && (
                  <p className="text-xs text-muted-foreground">
                    = {(parseInt(form.durata_mesi) / 12).toFixed(1)} anni - Quota mensile lineare dal mese della fattura
                  </p>
                )}
              </div>
            )}
            <div className="space-y-2">
              <Label>Conto Ammortamento</Label>
              <SearchableSelect
                options={[
                  { value: "__none__", label: "Nessuno" },
                  ...conti.map(c => ({ value: c.id, label: `${c.codice ? `[${c.codice}] ` : ""}${c.nome}` }))
                ]}
                value={form.conto_id}
                onValueChange={(v) => setForm({ ...form, conto_id: v })}
                placeholder={t("common.selectPlaceholder")}
                searchPlaceholder={t("common.searchPlaceholder")}
                testId="select-conto"
              />
              <p className="text-xs text-muted-foreground">
                Il conto su cui verranno registrati i costi di ammortamento nel bilancio
              </p>
            </div>
            <div className="space-y-2">
              <Label>{t("common.description")}</Label>
              <Textarea
                value={form.descrizione}
                onChange={(e) => setForm({ ...form, descrizione: e.target.value })}
                placeholder="Descrizione opzionale..."
                rows={3}
                data-testid="input-descrizione"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={handleSubmit} data-testid="save-btn">
              {editingId ? t("common.save") : "Crea Categoria"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, item: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("confirm.confirmDeletion")}</DialogTitle>
            <DialogDescription>
              Eliminare la categoria "{deleteDialog.item?.nome}"?
              Non sarà possibile se ci sono ammortamenti collegati.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, item: null })}>{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">{t('common.delete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
